import { KitRating } from '@/features/candidate/components/candidate-interview/candidate-interview-kit-evaluation/ui/kit-rating';
import { EvaluationEditor } from '@/features/candidate/components/candidate-interview/ui/evaluation-editor';
import { Icon, Switch } from '@/features/common';
import {
  createEditorState,
  EditorState,
} from '@/features/common/components/custom-text-editor';
import { BetterTextEditor } from '@/features/common/components/custom-text-editor/better-text-editor';
import { useAnalytics } from '@/features/common/hooks/use-analytics';
import { useNotifications } from '@/features/notifications';
import { useUpdateInterviewMutation } from '@/queries/interviews/mutations';
import { useGetInterview } from '@/queries/interviews/queries';
import { InterviewKit, InterviewSection } from '@/queries/types';
import styled from '@emotion/styled';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  DSButton,
  DSInput,
  DSModal,
  DSTextArea,
} from '@hundred5/design-system';
import { editorStateToHtml, htmlToEditorState } from '@togglhire/text-editor';
import { Form, Formik } from 'formik';
import React, { useRef } from 'react';

interface InterviewKitCreatorProps {
  workspaceId: number;
  openingId: number;
  stageId: number;
  open: boolean;
  onClose: () => void;
}

export function InterviewCreator({
  workspaceId,
  openingId,
  stageId,
  open,
  onClose,
}: InterviewKitCreatorProps) {
  const interviewQuery = useGetInterview(workspaceId, openingId, stageId);
  const updateInterviewMutation = useUpdateInterviewMutation({
    meta: { invalidates: 'all' },
  });
  const [isDirty, setIsDirty] = React.useState(false);
  const { addNotification } = useNotifications();
  const { trackEvent } = useAnalytics('interview');

  const initialValues: InterviewKit = interviewQuery.data?.kit ?? {
    isScorecard: true,
    sections: [
      {
        title: 'Introduction',
        description: '',
      },
      {
        title: 'Section 1',
        description: '',
      },
      {
        title: 'Candidate questions',
        description: '',
      },
    ],
  };

  const getAddNewButtonPositionAfterIndex = (sections?: InterviewSection[]) => {
    if (!sections || sections.length === 0) return 0;

    const lastIndex = sections.length - 1;
    const lastTitle = sections[lastIndex]?.title || '';

    if (lastTitle.toLowerCase().includes('candidate questions')) {
      return lastIndex - 1;
    }

    return lastIndex;
  };

  const handleSubmit = (values: InterviewKit) => {
    if (!interviewQuery.data) return;

    trackEvent('questions_saved', {
      workspace_id: workspaceId,
      job_opening_id: openingId,
      scorecard_turned_on: values.isScorecard ? 'yes' : 'no',
    });

    updateInterviewMutation.mutate(
      {
        workspaceId,
        openingId,
        stageId,
        data: {
          ...interviewQuery.data,
          kit: values,
        },
      },
      {
        onSuccess: () => {
          addNotification({
            type: 'saved',
          });
          onClose();
        },
      }
    );
  };

  return (
    <DSModal
      open={open}
      onClose={onClose}
      contentStyle={{ padding: 0, width: '100%', height: '100%' }}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <CloseButtonWrapper>
        <DSModal.CloseButton onClick={onClose} />
      </CloseButtonWrapper>
      <Header>
        <h1>{interviewQuery.data?.name}</h1>
      </Header>

      <Formik<InterviewKit>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <ContentForm>
            <Main>
              {values.sections?.map((section, index) => (
                <Section key={index}>
                  <SectionHeader>
                    <SectionTitle
                      value={section.title}
                      onChange={(e) => {
                        const updatedSections = [...(values.sections ?? [])];
                        if (e.target.value.length <= 32) {
                          updatedSections[index].title = e.target.value;
                          setFieldValue('sections', updatedSections);
                          setIsDirty(true);
                        }
                      }}
                    />
                    <ButtonContainer>
                      {values.isScorecard && (
                        <KitRating
                          selectedRating={undefined}
                          onRatingChange={() => {}}
                          section={section}
                          disabled
                        />
                      )}
                      <DSButton
                        data-label="delete-button"
                        Icon={
                          <Icon
                            icon={regular('trash')}
                            size="sm"
                            color="purple-100"
                          />
                        }
                        variant="tertiary"
                        data-rh="Delete section"
                        data-rh-at="top"
                        onClick={() => {
                          const updatedSections = [...(values.sections ?? [])];
                          updatedSections.splice(index, 1);
                          setFieldValue('sections', updatedSections);
                          setIsDirty(true);
                        }}
                      />
                    </ButtonContainer>
                  </SectionHeader>
                  <TextEditorInterview
                    className="rounded-lg p-2 w-full"
                    html={section.description || ''}
                    placeholder="Add your interview questions and guidance here..."
                    onChange={(state: EditorState) => {
                      const updatedSections = [...(values.sections ?? [])];
                      updatedSections[index].description =
                        editorStateToHtml(state);
                      setFieldValue('sections', updatedSections);
                      setIsDirty(true);
                    }}
                  />
                  {values.isScorecard && (
                    <StyledTextArea
                      className="w-full"
                      placeholder="This is where interviewers will write their comments"
                      disabled
                    />
                  )}
                  <Separator />
                  {index ===
                  getAddNewButtonPositionAfterIndex(values.sections) ? (
                    <>
                      <div>
                        <DSButton
                          Icon={
                            <Icon
                              icon={solid('plus')}
                              color="purple-80"
                              size={'xl'}
                            />
                          }
                          size="small"
                          variant="tertiary"
                          onClick={() => {
                            const updatedSections = [
                              ...(values.sections ?? []),
                            ];
                            updatedSections.splice(
                              getAddNewButtonPositionAfterIndex(
                                values.sections
                              ) + 1,
                              0,
                              { title: 'New section', description: '' }
                            );
                            setFieldValue('sections', updatedSections);
                            setIsDirty(true);
                          }}
                        >
                          <ButtonText>ADD SECTION</ButtonText>
                        </DSButton>
                      </div>
                      <Separator />
                    </>
                  ) : null}
                </Section>
              ))}
              <Section>
                <h3 className="mb-4 font-semibold ml-3">
                  Overall recommendation
                </h3>
                <StyledEvaluationEditor
                  onContentChange={() => {}}
                  onDecisionChange={() => {}}
                  content={createEditorState('')}
                  placeholder="This is where interviewers will provide their overall recommendation"
                  disabled
                />
              </Section>
            </Main>

            <Aside>
              <AsideLabel>Evaluation</AsideLabel>
              <Switch
                label="Turn into scorecard"
                value={values.isScorecard}
                onChange={() => {
                  setFieldValue('isScorecard', !values.isScorecard);
                  setIsDirty(true);
                }}
              />
            </Aside>

            <Footer>
              <DSButton variant="secondary" onClick={onClose}>
                Cancel
              </DSButton>
              <DSButton
                type="submit"
                variant="primary-purple"
                disabled={
                  isSubmitting ||
                  !isDirty ||
                  !!values.sections?.find((section) => !section.title)
                }
              >
                Save
              </DSButton>
            </Footer>
          </ContentForm>
        )}
      </Formik>
    </DSModal>
  );
}

export const TextEditorInterview = ({
  html,
  onChange,
  ...props
}: {
  html: string;
  onChange: (newState: EditorState) => void;
  [key: string]: any;
}) => {
  const editorStateRef = useRef<EditorState | null>(null);

  if (!!html && !editorStateRef.current) {
    editorStateRef.current = htmlToEditorState(html);
  } else if (!html) {
    editorStateRef.current = createEditorState('');
  }
  const editorState = editorStateRef.current;

  return (
    <TextEditor
      state={editorState || createEditorState('')}
      onChange={(newState) => onChange(newState)}
      resizable={false}
      {...props}
    />
  );
};

const Header = styled.header`
  padding: 36px 36px 16px 36px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.purple[10]};
  position: inherit;
  z-index: 10;

  h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 19px;
    color: ${({ theme }) => theme.typography.colorPrimary};
  }
`;

const ContentForm = styled(Form)`
  display: grid;
  grid-template-areas: 'main aside' 'footer footer';
  grid-template-columns: auto minmax(0, 272px);
  height: 100%;
`;

const Main = styled.main`
  grid-area: main;
  padding: 16px 52px 36px 36px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 280px);
`;

const Section = styled.div`
  padding-left: 0px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  margin-top: 24px;

  & button[data-label='delete-button'] {
    opacity: 0;
  }

  &:hover button[data-label='delete-button'] {
    opacity: 1;
  }
`;

const ButtonText = styled.div`
  color: ${({ theme }) => theme.colors.purple[80]};
`;

const SectionTitle = styled(DSInput)`
  display: flex;
  align-items: center;
  align-self: stretch;
  border: none;
  outline: none;
  margin-top: 0;
  margin-bottom: -12px;
  font-weight: 600;
  border: none;
  padding: 0 8px;
`;

const Separator = styled.div`
  margin: 24px 0;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.purple[10]};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  position: relative;
  left: 44px;
`;

const Aside = styled.aside`
  grid-area: aside;
  padding: 16px 12px;
  border-left: 1px solid ${({ theme }) => theme.colors.purple[10]};
`;

const AsideLabel = styled.div`
  color: ${({ theme }) => theme.typography.colorSecondary};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const Footer = styled.footer`
  grid-area: footer;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 36px 36px 36px;
  border-top: 1px solid ${({ theme }) => theme.colors.purple[10]};
`;

const TextEditor = styled(BetterTextEditor)`
  &:hover {
    background: ${({ theme }) => theme.colors.purple[5]};
  }

  > div:first-child {
    display: none;
  }

  > div:nth-child(2) {
    margin-top: 0;
  }
`;

const StyledEvaluationEditor = styled(EvaluationEditor)`
  margin-left: 12px;
`;

const StyledTextArea = styled(DSTextArea)`
  margin-top: 16px;
`;

const CloseButtonWrapper = styled.div`
  position: inherit;
  z-index: 20;
`;
